/* ============================================================
   MOSAIK — RENDER
   Draws the whole design onto the canvas: tiles, borders, corners,
   then any active feature layers (e.g. lights), then the recap.
   Pixel math lives in mosaik-geometry.js; this file only paints.
   ============================================================ */
Object.assign(window.mosaik, {

  /* The box the plan is fitted into, in CSS pixels. It is a TARGET, not a hard
     limit: mosaik-geometry.js keeps tiles clickable (50px floor), so a long
     floor comes out taller than this box — the stage card then scrolls rather
     than the plan being squeezed (see the split-view CSS in the template).

     Desktop split view, the two axes come from different elements on purpose:
       width  — #mosaik-canvas, stretched by the pane. Its width comes from the
                pane, never from the canvas inside it, so measuring it cannot
                feed back into the drawing size.
       height — #mosaik-stage minus the card chrome. The frame's HEIGHT now
                follows the plan, so measuring it here WOULD be circular (draw
                bigger -> frame taller -> draw bigger...). The stage is fixed by
                the shell, which makes it the stable equivalent: it is the height
                the plan gets for free, past which the card starts scrolling.

     Empilé (mobile, tablette), la LARGEUR se mesure aussi — sur le panneau, qui
     s'étend sur toute la rangée et ne se règle donc jamais sur le plan qu'il
     contient (voir §4 du CSS). Le viewport seul ne suffit pas : le panneau vit
     dans une carte, elle-même dans le shell, et chaque marge le rétrécit ; un
     plan dessiné à 90% du viewport y serait ramené de force par `max-width:100%`
     — la même image en plus petit, mais des dalles décalées sous le doigt, car
     les zones cliquables, elles, gardent la taille dessinée.

     La HAUTEUR reste une fraction du viewport : le panneau la tient de son
     contenu, la mesurer reviendrait à se mordre la queue.

     Partout ailleurs (avant la mise en page, capture PDF hors écran) : repli sur
     le LAYOUT viewport (documentElement.client*) plutôt que window.inner*. Sur
     iOS Safari, window.inner* suit le viewport VISUEL, donc rétrécit pendant que
     le visiteur zoome la page à deux doigts — un draw() lancé dans cet état
     dessinerait un plan minuscule (les inner* de Chrome ne bougent pas, d'où un
     bug qu'on ne voyait que sur Safari). */
  /* Le panneau RÉSERVE EN PADDING les deux bandes des règles (voir le CSS) :
     clientWidth / clientHeight les comptent, alors que le plan, lui, est ajusté
     dans ce qui reste — sans quoi il passerait sous les règles au repos. */
  frameInner:function(frame){
    const cs = getComputedStyle(frame);
    const px = p => parseFloat(cs[p]) || 0;
    const padL = px('paddingLeft'), padR = px('paddingRight'),
          padT = px('paddingTop'),  padB = px('paddingBottom');
    return { w: frame.clientWidth  - padL - padR,
             h: frame.clientHeight - padT - padB,
             padL, padR, padT, padB, padY: padT + padB };
  },

  /* Le plafond de la scène sur téléphone, en pixels CSS — le MÊME que celui posé
     en `max-height` sur #mosaik-stage (§4 du CSS) : ce qui reste du shell une
     fois retirées la barre d'action, la part minimale des réglages et les deux
     marges qui les séparent. Tout est mesuré ou lu, rien n'est deviné, et rien
     ne dépend du plan — c'est ce qui permet de dimensionner le plan avec.
     GAPS : la marge sous la scène et celle au-dessus de la barre (10 + 10). */
  MOBILE_GAPS: 20,
  mobileStageCap:function(){
    const shell = document.getElementById('mosaik-shell'),
          conf  = document.getElementById('mosaik-conf');
    const H = shell ? shell.clientHeight
                    : (document.documentElement.clientHeight || window.innerHeight);
    const num = (v, d) => { const n = parseFloat(v); return isFinite(n) ? n : d; };
    const cs = conf ? getComputedStyle(conf) : null;
    const menu = num(cs && cs.getPropertyValue('--mos-menu-min'), 170);
    const bar  = num(cs && cs.getPropertyValue('--mos-bar-h'), 92);
    /* Garde-fou de déploiement : les assets partent fichier par fichier, un
       visiteur peut donc avoir ce module-ci avec un template encore en cache,
       dont le shell est empilé et de hauteur libre — shell.clientHeight y vaut
       toute la page, et le plafond n'aurait plus aucun sens. Le plan ne prend
       jamais plus des trois quarts de la fenêtre : sur le template à jour le
       plafond est bien en dessous, cette borne ne se voit pas. */
    const vpH = document.documentElement.clientHeight || window.innerHeight;
    return Math.min(H - menu - bar - this.MOBILE_GAPS, vpH * 0.75);
  },

  drawBox:function(){
    const doc = document.documentElement;
    const vpW = doc.clientWidth  || window.innerWidth;
    const vpH = doc.clientHeight || window.innerHeight;
    const split  = window.matchMedia('(min-width:1025px)').matches;
    const mobile = window.matchMedia('(max-width:768px)').matches;
    /* LE PLAN EST AJUSTÉ DANS LA PLACE QUE LA MISE EN PAGE LUI DONNE, partout où
       elle lui en donne une : les deux panneaux du bureau, et l'écran partagé du
       téléphone (le plan, la fenêtre des réglages qui défile pour elle seule, la
       barre d'action — voir §4 du CSS).

       Le mobile ne devine plus une fraction de la fenêtre (0,6 × sa hauteur) :
       cette fraction datait de la page qui défilait d'un bloc, où le plan
       s'allongeait à sa guise. L'écran étant maintenant partagé, deviner
       reviendrait à déborder sur les réglages ou à laisser du panneau vide.

       LA HAUTEUR NE SE LIT PAS AU MÊME ENDROIT SUR LES DEUX ÉCRANS. Au bureau la
       scène a une hauteur à elle, imposée par la grille : on la mesure. Sur
       téléphone elle prend celle de son contenu — c'est-à-dire du plan qu'on est
       en train de dimensionner — et la mesurer tournerait en rond ; on refait
       donc le PLAFOND que le CSS lui pose, à partir de choses qui ne dépendent
       pas du plan : la hauteur du shell, la barre d'action, la part minimale des
       réglages. Les deux calculs doivent rester en phase — d'où la variable
       --mos-menu-min, lue ici et posée là-bas.

       Reste la tablette empilée, où la scène se règle sur son contenu SANS
       plafond : rien à mesurer ni à refaire, d'où le repli en fin de fonction. */
    if(split || mobile){
      const frame = document.getElementById('mosaik-canvas'),
            stage = document.getElementById('mosaik-stage');
      if(frame && stage){
        // PAD: keep the plan clear of the pane's edges (and, on the desktop, of a
        // scrollbar appearing) so a redraw can never oscillate between two sizes.
        // Wide enough there to also read as a margin of white panel around the
        // plan — un téléphone n'a pas cette place à donner, chaque pixel de marge
        // y est une dalle en moins.
        const PAD = mobile ? 10 : 28;
        const inner = this.frameInner(frame);
        const w = inner.w - PAD;
        // la bande du haut mange autant de hauteur au plan qu'elle en occupe
        let h = (mobile ? this.mobileStageCap() : stage.clientHeight) - PAD - inner.padY;
        const card = stage.querySelector('.mosaik-stage-card');
        if(card){
          const cs = getComputedStyle(card);
          h -= (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
        }
        // les rangées qui partagent la carte avec le plan (marges comprises —
        // #mosaik-entrance en a, et offsetHeight les laisse dehors).
        // La rangée du haut porte le libellé de largeur ET la barre de zoom, plus
        // haute que le libellé seul : c'est ELLE qu'on déduit. Repli sur le seul
        // libellé pour un template encore en cache, qui n'a pas la rangée.
        // La mention de déplacement ne sort que sur mobile, et seulement quand le
        // plan déborde : ailleurs elle est en display:none, donc offsetHeight 0.
        const top = document.getElementById('mosaik-plan-topline')
                      ? 'mosaik-plan-topline' : 'mosaik-width-label';
        [top, 'mosaik-entrance', 'mosaik-plan-hint'].forEach(id => {
          const el = document.getElementById(id);
          if(!el) return;
          const s = getComputedStyle(el);
          h -= el.offsetHeight + (parseFloat(s.marginTop) || 0) + (parseFloat(s.marginBottom) || 0);
        });
        // le seuil est plus bas sur un téléphone : le panneau y est réellement
        // petit, et retomber sur le repli (0,9 × 0,6 de la fenêtre) dessinerait un
        // plan sans rapport avec la boîte qui l'accueille
        const MIN = mobile ? 80 : 120;
        if(w > MIN && h > MIN) return mobile ? { w, h, mobile:true } : { w, h };
      }
    }
    return { w: vpW * 0.9, h: vpH * 0.6 };
  },

  /* ---------------------------------------------------------------
     MOBILE : LE PLAN PASSE AVANT LE CADRE

     Tenu dans la largeur d'un téléphone, un sol courant (8 × 4 m, soit 16 dalles
     de large) sort avec des dalles de 18 px : on voit tout le plan, mais on ne
     peut plus viser dedans — et le plan n'occupe qu'un quart de l'écran pendant
     que le reste attend en bas.

     Quand l'ajustement donne des dalles sous MOBILE_MIN_TILE, le plan est donc
     dessiné PLUS LARGE que son panneau (jusqu'à MOBILE_SPREAD_MAX fois), le
     panneau montre ce qu'il peut, et le reste s'amène sous le pouce à deux
     doigts — le geste que mosaik-zoom.js autorise déjà à 1:1, précisément pour
     rapprocher une bordure du centre de l'écran.

     Le débordement n'est jamais gratuit : il s'arrête à la dalle confortable, et
     un sol qui tient déjà correctement dans le panneau (peu de dalles, ou plus
     haut que large — c'est alors la hauteur qui borne) est dessiné comme avant,
     entier. Voir markOverflow() pour la mention affichée sous le plan. --------- */
  MOBILE_MIN_TILE: 34,
  MOBILE_SPREAD_MAX: 1.6,

  mobileLayout:function(box){
    // le plancher de dalle du téléphone : voir MOBILE_MIN_TILE ci-dessus et le
    // paramètre minTile de computeLayout (0 = le plancher par défaut, 50 px)
    const min = box.mobile ? this.MOBILE_MIN_TILE : 0;
    const L = MosaikGeometry.computeLayout(this.options, box.w, box.h, min);
    if(!box.mobile || !(L.tileW > 0) || L.tileW >= this.MOBILE_MIN_TILE) return L;
    const k = Math.min(this.MOBILE_SPREAD_MAX, this.MOBILE_MIN_TILE / L.tileW);
    return MosaikGeometry.computeLayout(this.options, box.w * k, box.h, min);
  },

  /* Le plan dépasse-t-il de son panneau ? C'est le seul cas où il faut le
     déplacer pour en voir le bout : la mention sous le plan (#mosaik-plan-hint,
     mobile uniquement) ne s'affiche que là. */
  markOverflow:function(cW, cH){
    const stage = document.getElementById('mosaik-stage'),
          frame = document.getElementById('mosaik-canvas');
    if(!stage || !frame) return;
    // LES DEUX AXES. La largeur était la seule à pouvoir déborder du temps où le
    // panneau s'allongeait avec le plan ; il a maintenant une hauteur à lui
    // (l'écran du téléphone est partagé, §4 du CSS), et un sol tout en longueur
    // passe donc aussi sous le bord bas — MosaikGeometry garde les dalles à 50 px
    // pour qu'elles restent visables plutôt que de tout faire entrer.
    const cut = cW > frame.clientWidth + 1
             || (cH > 0 && cH > this.frameInner(frame).h + 1);
    stage.classList.toggle('mosaik-plan-cut', cut);
  },

  /* ---------------------------------------------------------------
     BACKING-STORE RESOLUTION

     The <canvas> is LAID OUT at the plan's CSS size, but its bitmap is drawn
     `renderScale()` times bigger, and the context is scaled by the same factor
     so every coordinate below stays in CSS pixels.

     Two things make that necessary:
       - the screen's pixel density (a 1:1 canvas is already soft on a retina
         display);
       - the zoom, which is a CSS transform on the <canvas> (mosaik-zoom.js) —
         i.e. a plain upscale of whatever bitmap is there. A tile drawn into 20
         CSS px stays a 20 px raster however deep you zoom, and the more tiles
         the plan has, the smaller each one is drawn: that is exactly the "big
         floor + zoom = mush" case. Drawing at density × zoom makes the detail
         real; SVG tiles are re-rasterized by the browser at the destination
         size, so they come out sharp rather than magnified.

     Capped two ways: MAX_K (past ~8× there is nothing left to reveal) and a
     total pixel budget — a big floor at deep zoom would otherwise ask for a
     bitmap the browser refuses to allocate (iOS Safari gives up around 16M px)
     and every repaint would crawl. --------------------------------------- */
  MAX_K: 8,
  MAX_PIXELS: 24e6,          // desktop bitmap budget (~96 MB RGBA)
  MAX_PIXELS_MOBILE: 8e6,

  renderScale:function(cW, cH){
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const Z = window.MosaikZoom;
    const zoom = (Z && Z.currentScale) ? Math.max(1, Z.currentScale()) : 1;
    const budget = window.matchMedia('(max-width:768px)').matches
      ? this.MAX_PIXELS_MOBILE : this.MAX_PIXELS;
    let k = Math.min(this.MAX_K, dpr * zoom);
    const area = Math.max(1, cW * cH);
    if(area * k * k > budget) k = Math.sqrt(budget / area);
    return Math.max(1, k);
  },

  /* ---------------------------------------------------------------
     LES DEUX RÈGLES (largeur en haut, longueur à droite)

     Elles remplacent les anciennes étiquettes « Largeur : 8 m » / « Longueur :
     4 m », qui donnaient la dimension DEMANDÉE sans jamais dire où l'on en est
     dans le plan. Ce qu'elles mesurent est la surface DALLÉE — cols × la taille
     de dalle de la collection (mosaik-data.js) — donc le sol réellement dessiné,
     bordures exclues (leur dimension réelle n'est pas dans les données).

     ELLES NE SONT PAS DANS LE PLAN. Deux petits canvas fixés aux bords du
     PANNEAU (#mosaik-canvas), redessinés à chaque changement de zoom ou de
     déplacement — c'est mosaik-zoom.js qui les rappelle, par dSync(). Dessinées
     dans le plan, elles s'en iraient avec lui : zoomé au milieu de son sol, le
     visiteur n'aurait plus une seule mesure à l'écran, alors que c'est là qu'il
     en a le plus besoin. Fixées au panneau, la graduation qui passe devant ses
     yeux est TOUJOURS celle de ce qu'il regarde.

     Le panneau réserve leur place en padding (CSS) : au repos le plan est ajusté
     dans ce qui reste et ne passe jamais dessous. Zoomé, il glisse sous les
     bandes — d'où leur fond opaque, et `pointer-events:none` pour qu'un clic
     tombe malgré tout sur la dalle qui se trouve dessous. ------------------- */
  RULER_INK:  '#6b7a80',      // chiffres et crans : lisibles, jamais dominants
  RULER_BG:   '#fff',         // le fond du panneau : la bande ne se voit pas au repos
  RULER_LINE: '#e2e7e9',      // filet entre la bande et le plan

  /* Étiquette d'un cran : « 2 m », et la virgule décimale en français.
     La mesure du bout tombe entre deux crans ronds (un sol de 8 m posé en dalles
     de 39,5 cm en fait 8,295) : elle est donnée au CENTIMÈTRE, « 8,30 m », et
     non arrondie au décimètre comme le ferait l'écriture courte. */
  rulerLabel:function(m, exact){
    const lang = (this.options.lang === 'fr') ? 'fr-FR' : 'en-US';
    // ... sauf quand le sol tombe juste (dalles de 50 cm) : « 8 m », pas « 8,00 m »
    return m.toLocaleString(lang, (exact && !Number.isInteger(m))
      ? { minimumFractionDigits:2, maximumFractionDigits:2 }
      : { maximumFractionDigits:2 }) + ' m';
  },

  /* La bande, créée à la volée dans le panneau. `data-html2canvas-ignore` n'y
     est PAS : le devis PDF doit porter les mesures, et l'export ramène le plan
     à 1:1 avant la capture (mosaik-pdf.js), donc ce sont bien les graduations du
     sol entier qui y passent. */
  _rulerStrip:function(axis){
    const frame = this.grid && this.grid.parentElement;
    if(!frame) return null;
    const id = 'mosaik-ruler-' + axis;
    let el = document.getElementById(id);
    if(!el){
      el = document.createElement('canvas');
      el.id = id;
      el.className = 'mosaik-ruler';
      frame.appendChild(el);
    } else if(el.parentElement !== frame){
      frame.appendChild(el);        // le PDF déplace le plan : la bande le suit
    }
    return el;
  },

  /* Position À L'ÉCRAN (dans le panneau) d'un point du plan, transformation du
     zoom comprise : le <canvas> est translaté puis mis à l'échelle, origine en
     haut à gauche (transform-origin:0 0, voir le CSS et mosaik-zoom.js). */
  _planView:function(){
    const cv = this.grid;
    const v = (window.MosaikZoom && MosaikZoom.viewport)
      ? MosaikZoom.viewport() : { s:1, tx:0, ty:0 };
    return { s:v.s, x0:cv.offsetLeft + v.tx, y0:cv.offsetTop + v.ty };
  },

  syncRulers:function(){
    const L = this.layout, cv = this.grid;
    if(!L || !cv || !(L.cols > 0) || !(L.rows > 0)) return;
    const frame = cv.parentElement;
    if(!frame) return;
    const compact = window.matchMedia('(max-width:768px)').matches;
    const R = compact ? MosaikGeometry.RULER_COMPACT : MosaikGeometry.RULER;
    const view = this._planView();
    // portée mesurée = les dalles seules (le plan déborde des bandes de bordure)
    const spans = {
      x: { m:L.cols * this.options.mosaikWidth,  px:L.cols * L.tileW * view.s,
           from:view.x0 + L.xOff * view.s },
      y: { m:L.rows * this.options.mosaikHeight, px:L.rows * L.tileH * view.s,
           from:view.y0 + L.yOff * view.s }
    };
    this._drawStrip('x', R, spans.x, frame, compact);
    this._drawStrip('y', R, spans.y, frame, compact);
  },

  /* Une bande. `span.from` est l'abscisse (ou l'ordonnée) ÉCRAN du zéro de la
     règle, `span.px` la longueur écran de la portée : tout le reste en découle,
     et les crans hors panneau ne sont simplement pas tracés. */
  _drawStrip:function(axis, R, span, frame, compact){
    const el = this._rulerStrip(axis);
    if(!el) return;
    const horiz = (axis === 'x');
    // taille CSS de la bande : toute la largeur (ou la hauteur) du panneau
    const W = horiz ? frame.clientWidth : R.w,
          H = horiz ? R.h : frame.clientHeight;
    el.style.width = W + 'px'; el.style.height = H + 'px';
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    if(el.width !== Math.round(W * dpr) || el.height !== Math.round(H * dpr)){
      el.width = Math.round(W * dpr); el.height = Math.round(H * dpr);
    }
    const ctx = el.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = this.RULER_BG;
    ctx.fillRect(0, 0, W, H);
    // le filet côté plan, qui donne à la bande son bord et aux crans leur appui
    const base = horiz ? H : 0;                 // ligne de base, côté plan
    ctx.strokeStyle = this.RULER_LINE;
    ctx.lineWidth = 1;
    ctx.beginPath();
    if(horiz){ ctx.moveTo(0, base - 0.5); ctx.lineTo(W, base - 0.5); }
    else     { ctx.moveTo(base + 0.5, 0); ctx.lineTo(base + 0.5, H); }
    ctx.stroke();

    if(!(span.m > 0) || !(span.px > 0)) return;
    const font = (compact ? '600 10px ' : '600 11px ') + 'Arial, Helvetica, sans-serif',
          MAJ = compact ? 7 : 9,      // longueur du cran chiffré
          MIN = compact ? 4 : 5;      // longueur du cran de 50 cm
    ctx.font = font;
    const need = Math.ceil(ctx.measureText(this.rulerLabel(span.m, true)).width) + 12;
    const limit = horiz ? W : H;
    MosaikGeometry.rulerTicks(span.m, span.px, need).forEach(t => {
      const p = Math.round(span.from + t.pos) + 0.5;
      if(p < -1 || p > limit + 1) return;                 // hors du panneau
      const len = (t.kind === 'minor') ? MIN : MAJ;
      ctx.strokeStyle = this.RULER_INK;
      ctx.beginPath();
      if(horiz){ ctx.moveTo(p, base - len); ctx.lineTo(p, base); }
      else     { ctx.moveTo(base, p);       ctx.lineTo(base + len, p); }
      ctx.stroke();
      if(t.kind === 'minor' || t.m === 0) return;         // 0 : c'est le bord du plan
      ctx.fillStyle = this.RULER_INK;
      const txt = this.rulerLabel(t.m, t.kind === 'end');
      if(horiz){
        // une étiquette centrée sur un cran du bord sortirait de la bande : elle
        // se range alors du côté où il reste de la place
        const half = ctx.measureText(txt).width / 2;
        ctx.textBaseline = 'alphabetic';
        ctx.textAlign = (p + half > W) ? 'right' : (p - half < 0 ? 'left' : 'center');
        ctx.fillText(txt, p, base - len - 3);
      } else {
        /* Étiquettes pivotées d'un quart de tour, comme l'ancienne « Longueur :
           4 m » : la bande n'a alors à porter que la HAUTEUR d'une ligne de
           texte, là où de l'horizontal lui coûterait sa largeur — sur un
           téléphone, autant de dalles en moins. */
        const w = ctx.measureText(txt).width;
        ctx.save();
        ctx.translate(base + len + 3, p);
        ctx.rotate(-Math.PI / 2);
        ctx.textBaseline = 'top';
        ctx.textAlign = (p - w < 0) ? 'right' : (p + w > H ? 'left' : 'center');
        ctx.fillText(txt, 0, 0);
        ctx.restore();
      }
    });
  },

  /* ---------------------------------------------------------------
     LA DÉLIMITATION DES BORDURES ET DES ANGLES

     Les cases de bordure et d'angle ENCORE VIDES ne sont qu'une aide à la pose :
     le quadrillage gris qui les dessine ne dit rien du sol créé, il montre où
     l'on POURRAIT poser. Affiché en permanence, il encadre le plan d'un liseré
     que le visiteur n'a pas demandé — et le devis PDF, qui reprend le canvas tel
     quel, le porte aussi.

     Il n'est donc tracé que pendant que la carte « Bordures et angles » est
     dépliée, c'est-à-dire au moment précis où il sert — LE CADRE NOIR DU PLAN
     COMPRIS : il ceint les bandes de bordure, pas le sol dallé, et laisserait
     sans elles une gouttière blanche cernée de noir. Les cases DÉJÀ COLORÉES ne
     sont pas concernées : elles font partie du dessin, et restent visibles la
     carte refermée comme n'importe quelle dalle.

     La carte repliée porte la classe `collapsed` (posée par mosaik-ui.js, qui
     redessine à chaque pliage) ; elle est masquée en display:none quand la
     collection n'a ni bordure ni angle (refreshCards). Pas de carte du tout
     (aperçu local, gabarit d'avant encore en cache) : on garde l'ancien
     comportement plutôt que de faire disparaître la délimitation sans recours. */
  edgeGuidesOn:function(){
    const card = document.getElementById('mosaik-card-edges');
    if(!card) return true;
    return !card.classList.contains('collapsed') && card.style.display !== 'none';
  },

  draw:async function(){
    // -- read inputs & compute layout --
    this.options.width  = parseFloat(document.getElementById('mosaik-area-width').value)  || 0;
    this.options.height = parseFloat(document.getElementById('mosaik-area-height').value) || 0;

    const box = this.drawBox();
    const layout = this.mobileLayout(box);
    this.layout = layout;
    const { cols, rows, tileW, tileH, bW, bH, cW, cH, xOff, yOff } = layout;

    // Bitmap in device pixels, layout box in CSS pixels. Everything downstream
    // (hit tests, zoom/pan clamps, PDF capture) keeps working in CSS pixels —
    // paintAt() is the one place that has to convert (see mosaik-controller.js).
    const k = this.renderScale(cW, cH);
    this.rscale = k;
    this.grid.width  = Math.round(cW * k);
    this.grid.height = Math.round(cH * k);
    this.grid.style.width  = cW + 'px';
    this.grid.style.height = cH + 'px';
    this.markOverflow(cW, cH);
    this.ctx.reset();
    this.ctx.setTransform(k, 0, 0, k, 0, 0);   // AFTER reset(), which clears it
    this.ctx.imageSmoothingQuality = 'high';   // photo tiles are downscaled to the cell

    // -- draw tiles --
    /* LES OBJETS POSÉS SUR LE PLAN NE SONT PAS PEINTS DANS `result`.
       Un encadrement de voiture impose la couleur des dalles de son contour, et
       la rend telles qu'elles étaient dès qu'on le retire (mosaik-objects.js) :
       la substitution se fait donc ICI, au dernier moment, et jamais dans
       l'état du dessin. Table vide quand il n'y a aucun objet. */
    /* `tileOverrides` vit dans mosaik-core.js. Les assets partent fichier par
       fichier : ce module-ci peut donc tourner sur un core d'une génération
       antérieure, qui ne l'a pas — et un `this.tileOverrides is not a function`
       au milieu de draw() ne laisse RIEN à l'écran, pas seulement les objets.
       On interroge alors la couche d'objets directement : c'est ce que le core
       à jour fait de toute façon. */
    const OV = this.tileOverrides ? this.tileOverrides()
      : ((window.MosaikObjects && MosaikObjects.enabled) ? MosaikObjects.tileOverrides(this) : null);
    /* Ce drapeau dit à la couche d'objets « le plan sait te peindre ». Les
       assets partent fichier par fichier : sans lui, un mosaik-render2.js d'une
       génération antérieure ne consulterait pas la table ci-dessus, et un
       encadrement posé resterait invisible sans que rien ne le signale. La
       couche d'objets se rabat alors sur son propre calque (voir la section
       « filet de déploiement » dans mosaik-objects.js). */
    this._objectsPainted = true;
    this.paths = [];
    let idx = 0;
    for(let i = 0;i < rows; i++){
      for(let j = 0; j < cols; j++, idx++){
        // empty cells show the BACKGROUND pile (black) — not the brush (currentPile) —
        // so the grid starts black while the default brush stays dark grey.
        if(this.result[idx] === undefined){
            const bg = this.options.bgPile || this.options.currentPile;
            if(bg) this.result[idx] = bg;
        }
        // une dalle SUPPRIMÉE (false) le reste : un objet ne recrée pas de sol
        let cell = this.result[idx];
        if(cell && OV) cell = OV.get(idx) || cell;
        if(cell){
          this.ctx.fillStyle = cell.color;
          this.ctx.strokeStyle = 'white';
          if(this.options.fill === 'image'){
            if(!cell.obj.complete)
                await this.waitImg(cell.obj);
            const nw = cell.obj.naturalWidth, nh = cell.obj.naturalHeight;
            // image manquante / en échec (naturalWidth 0) : on retombe sur la
            // couleur pleine de la dalle plutôt que de laisser drawImage lever une
            // exception (qui viderait tout le canvas).
            if(nw === 0 || nh === 0){
              this.ctx.fillRect(xOff+j*tileW, yOff+i*tileH, tileW, tileH);
            } else {
            // cutPercent trims the margin of PHOTO tiles. SVGs are already edge-to-edge
            // and (having a viewBox but no width/height) report an unreliable natural
            // size, so never crop them — draw the whole image scaled into the cell.
            const isSvg = /\.svg(\?|#|$)/i.test(cell.img || cell.obj.currentSrc || cell.obj.src || '');
            // back the cell with the tile colour first, so transparent SVGs sit on it
            // (invisible under opaque photos)
            if(isSvg) this.ctx.fillRect(xOff+j*tileW, yOff+i*tileH, tileW, tileH);
            if(cell.cutPercent && !isSvg && nw > 0 && nh > 0){
              const sx=Math.round(nw*cell.cutPercent/100),
                    sy=Math.round(nh*cell.cutPercent/100),
                    sw=nw-2*sx, sh=nh-2*sy;
              this.ctx.drawImage(cell.obj,sx,sy,sw,sh,xOff+j*tileW,yOff+i*tileH,tileW,tileH);
            }else this.ctx.drawImage(cell.obj,xOff+j*tileW,yOff+i*tileH,tileW,tileH);
            }
          }else this.ctx.fillRect(xOff + j * tileW,yOff+i*tileH,tileW,tileH);
          this.ctx.strokeRect(xOff+j*tileW,yOff+i*tileH,tileW,tileH);
        }
        const p=new Path2D();
        p.rect(xOff+j*tileW,yOff+i*tileH,tileW,tileH);
        this.paths.push({path:p,idx:idx});
      }
    }

    // -- draw borders --
    // Les cases vides ne sont tracées que carte « Bordures et angles » ouverte
    // (voir edgeGuidesOn) ; les zones cliquables, elles, sont TOUJOURS montées —
    // le plan garde exactement la même géométrie, seule la délimitation s'efface.
    const guides = this.edgeGuidesOn();
    const bCells = MosaikGeometry.buildBorderCells(layout);
    if(guides){
      this.ctx.strokeStyle='#ccc';
      bCells.forEach(c => this.ctx.strokeRect(c.x, c.y, c.w, c.h));
    }
    this.borderPaths=[];
    for(let c of bCells){
      const bo = this.borderAssignments[c.idx];
      if(bo){
        this.ctx.fillStyle = bo.color;
        this.ctx.fillRect(c.x,c.y,c.w,c.h);
        this.ctx.strokeStyle = 'white';
        this.ctx.strokeRect(c.x,c.y,c.w,c.h);
      }
      const p=new Path2D();
      p.rect(c.x,c.y,c.w,c.h);
      this.borderPaths.push({
        path : p,
        idx : c.idx,
        side : c.side
    });
    }

    // -- draw corners (4) --
    const cornerCells = MosaikGeometry.buildCornerCells(layout);
    if(guides){
      this.ctx.strokeStyle='#ccc';
      cornerCells.forEach(c=>this.ctx.strokeRect(c.x,c.y,c.w,c.h));
    }
    this.cornerPaths=[];
    for(let c of cornerCells){
      const co=this.cornerAssignments[c.idx];
      if(co){
        this.ctx.fillStyle=co.color;this.ctx.fillRect(c.x,c.y,c.w,c.h);
        this.ctx.strokeStyle='white';this.ctx.strokeRect(c.x,c.y,c.w,c.h);
      }
      const p=new Path2D();
      p.rect(c.x,c.y,c.w,c.h);
      this.cornerPaths.push({
        path:p,
        idx:c.idx
    });
    }

    // -- le cadre du plan --
    // Il était porté par le <canvas> lui-même (border CSS) ; le canvas contient
    // désormais les gouttières des règles, où ce cadre n'a rien à faire. Tracé
    // ici, il ceint le PLAN, comme avant.
    //
    // IL FAIT PARTIE DE LA DÉLIMITATION, et disparaît donc avec elle : ce qu'il
    // ceint n'est pas le sol dallé mais le sol AVEC ses bandes de bordure. La
    // carte refermée, ces bandes ne se voient plus — le cadre laisserait alors
    // une gouttière blanche d'un tiers de dalle cernée de noir tout autour du
    // plan, c'est-à-dire exactement le cadre qu'on venait de retirer.
    if(guides){
      this.ctx.strokeStyle = '#000';
      this.ctx.lineWidth = 1;
      this.ctx.strokeRect(layout.ox + 0.5, layout.oy + 0.5, layout.planW - 1, layout.planH - 1);
    }

    // -- les règles graduées, dans leurs bandes du panneau --
    // (le plan vient de changer de taille : leurs crans aussi)
    this.syncRulers();

    // -- feature layers (draw on top of tiles) --
    if(window.MosaikLights && MosaikLights.enabled) MosaikLights.draw(this, layout);
    // les objets déposés : seuls les ACCESSOIRES se dessinent ici (les
    // encadrements sont des dalles, déjà peintes plus haut via OV). Leur calque
    // de poignées, lui, vit hors du plan — comme celui de la sélection.
    if(window.MosaikObjects && MosaikObjects.enabled){
      MosaikObjects.draw(this, layout);
      MosaikObjects.render();
    }
    // la zone sélectionnée vit sur son propre calque, au-dessus du plan : le
    // nouveau tracé peut l'avoir déplacée (taille des dalles) — voir mosaik-select.js
    if(window.MosaikSelect) MosaikSelect.render();

    // -- recap --
    this.calcResult();

    // draw() is async (it awaits the tile images), so the plan's final size is
    // only known here — and the zoom toolbar's enabled state is derived from it
    // (a plan bigger than its panel is pannable even at 1:1).
    if(window.MosaikZoom) MosaikZoom.dSync();
  }

});
